import { z } from 'zod/v4';
import { RULE_ASSET_TYPES } from '../../constants.js';

/**
 * The nested trigger/offset shapes are a union that varies by connector type
 * (`equals`, `oneOf`, `allOf`, ...) — `assetType`/`asset` can be a single
 * `{key, text}` pair, an array of them, or a `{key, list}` wrapper. Modelled
 * loosely rather than as a full discriminated union: this response schema exists
 * for API docs/validation, not as the single source of truth for the shape (the
 * rule-engine transform in `core/services/rule-templates/rule-engine/` is that).
 */
const KeyTextSchema = z.object({
  key: z.union([z.string(), z.number(), z.array(z.string())]),
  text: z.string().optional(),
});

export const RuleTemplateTriggerSchema = z.object({
  id: z.string(),
  idx: z.number(),
  connectorType: KeyTextSchema,
  dataPoint: KeyTextSchema,
  assetType: z.unknown(),
  asset: z.unknown().optional(),
  operator: KeyTextSchema.optional(),
});

export const RuleTemplateOffsetSchema = z.object({
  id: z.string(),
  offsetType: KeyTextSchema.optional(),
  metrics: KeyTextSchema.optional(),
  trueValue: z.number(),
  falseValue: z.number(),
  riskFactors: z.array(
    z.object({ id: z.string(), key: z.string(), text: z.string().optional() }),
  ),
});

export const RuleTemplateRuleSchema = z.object({
  id: z.string(),
  idx: z.number(),
  triggers: z.array(RuleTemplateTriggerSchema),
  offsets: z.array(RuleTemplateOffsetSchema),
});

/** `GET /risk-analysis/rule-templates/:id` response, ported from the legacy
 * `get/index.js` handler's `{...ruleTemplate, rules, mismatchedDatapoint}`. */
export const RuleTemplateResourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  visibility: z.string(),
  description: z.string().nullable(),
  scoreFormula: z.string().nullable(),
  status: z.string(),
  tags: z.array(z.string()).nullable(),
  version: z.number(),
  type: z.string(),
  isActive: z.number(),
  defaultSeverity: z.number().nullable(),
  createdAt: z.string(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /** The last editor, from the template's history; null until its first save. */
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyId: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
  rules: z.array(RuleTemplateRuleSchema),
  mismatchedDatapoint: z.boolean(),
});

export type RuleTemplateResource = z.infer<typeof RuleTemplateResourceSchema>;

export const GetRuleTemplateParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

/**
 * Flat rule-template row shape — ported from the legacy `buildTemplate`
 * transform applied by `dataGet.getRuleTemplate`
 * (`2025-06/risk-analysis-service/data/rule-templates/get.js`). Unlike
 * `RuleTemplateResourceSchema` (the `GET /:id` shape), this carries no
 * `rules`/`mismatchedDatapoint` — it's the bare row, which is what
 * `POST /risk-analysis/rule-templates` sends back as-is.
 */
export const RuleTemplateRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  visibility: z.string(),
  description: z.string().nullable(),
  scoreFormula: z.string().nullable(),
  status: z.string(),
  tags: z.array(z.string()).nullable(),
  version: z.number(),
  type: z.string(),
  isActive: z.number(),
  defaultSeverity: z.number().nullable(),
  createdAt: z.string(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /** The last editor, from the template's history; null until its first save. */
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyId: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
});

export type RuleTemplateRowResource = z.infer<typeof RuleTemplateRowSchema>;

/**
 * `POST /risk-analysis/rule-templates` body, ported from the legacy
 * `ruleTemplateSchema.create.body`
 * (`2025-06/risk-analysis-service/routes/schemas/v3/request/rule-template.js`).
 * The legacy handler destructures only `{title}` — any other field a caller
 * sends (e.g. `visibility`) is accepted-but-ignored noise in the legacy schema,
 * so it's dropped here rather than carried into the new contract.
 */
export const CreateRuleTemplateBodySchema = z.object({
  title: z.string().min(1).max(255),
});

export type CreateRuleTemplateBody = z.infer<
  typeof CreateRuleTemplateBodySchema
>;

/**
 * `POST /risk-analysis/rule-templates` response — kept as the legacy flat row
 * (`reply.code(201).send(createdRuleTemplate)`) rather than the `apiSuccess`
 * `{data, metadata}` envelope, because existing frontend callers read
 * `response.id`/`response.data.id` directly (see
 * `frontend-v2/src/api/risk/types.ts`'s `CreateRiskRuleResponse`).
 */
export const CreateRuleTemplateResponseSchema = RuleTemplateRowSchema;

/**
 * `POST /risk-analysis/rule-templates/copy` body, ported from the legacy
 * `ruleTemplateSchema.copy.body`
 * (`2025-06/risk-analysis-service/routes/schemas/v3/request/rule-template.js`).
 * Unconstrained array length on purpose — the legacy schema has none either,
 * and an empty `ids` array is expected to reach the handler (see
 * `post.copy.integration.test.ts`'s `IN ()` parity test).
 */
export const CopyRuleTemplatesBodySchema = z.object({
  ids: z.array(z.string().min(1).max(36)),
});

export type CopyRuleTemplatesBody = z.infer<typeof CopyRuleTemplatesBodySchema>;

/**
 * `POST /risk-analysis/rule-templates/copy` response — kept as the legacy flat
 * `{message}` body (`reply.code(201).send({message: 'Copied Successfully'})`)
 * rather than the `apiSuccess` `{data, metadata}` envelope, because existing
 * frontend callers read `response.data.message` directly (see
 * `frontend/src/views/risk-analysis/partials/risk-rules/list/actions/index.jsx`).
 */
export const CopyRuleTemplatesResponseSchema = z.object({
  message: z.string(),
});

export const RULE_TEMPLATES_LIST_DEFAULT_PAGE_SIZE = 15;
export const RULE_TEMPLATES_LIST_MAX_PAGE_SIZE = 500;
export const RULE_TEMPLATES_LIST_FILTER_MAX_LENGTH = 512;

/**
 * Sortable columns of `GET /risk-analysis/rule-templates`. Legacy mapped only
 * `title` (to an alias the query did not have — every title sort was a 500),
 * `riskCount`, `blueprintCount` and `updatedAt`, and silently ignored the rest
 * of what both frontends offer. `createdAt` is new, and is the default order
 * (newest first), so a freshly cloned template — whose `updatedAt` stays null
 * until its first save — lands at the top of the list. Keys must stay in sync
 * with `SORT_COLUMNS` in `core/db/rule-templates/list.ts`.
 */
export const RuleTemplatesListSortColumns = [
  'title',
  'riskCount',
  'ruleCount',
  'blueprintCount',
  'createdAt',
  'updatedAt',
  'visibility',
  'status',
  'isActive',
] as const;

const sortSegment = `(${RuleTemplatesListSortColumns.join('|')}):(ASC|DESC|asc|desc)`;

/** An empty query value means "not set", as legacy treated it. */
const blankAsUndefined = (value: unknown) => (value === '' ? undefined : value);

/**
 * `GET /risk-analysis/rule-templates` querystring, ported from `list.querystring`
 * in `2025-06/risk-analysis-service/routes/schemas/v3/request/rule-template.js`.
 *
 * Strict, unlike legacy AJV, which silently stripped unknown params. `sort` is
 * one or more comma-separated `column:DIRECTION` pairs over
 * `RuleTemplatesListSortColumns`; legacy accepted any string and interpolated
 * its direction into the SQL unescaped.
 */
export const RuleTemplatesListQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(RULE_TEMPLATES_LIST_MAX_PAGE_SIZE)
    .default(RULE_TEMPLATES_LIST_DEFAULT_PAGE_SIZE),
  view: z.preprocess(
    blankAsUndefined,
    z.enum(['RETRACED', 'CUSTOM']).optional(),
  ),
  filter: z.string().max(RULE_TEMPLATES_LIST_FILTER_MAX_LENGTH).optional(),
  sort: z.preprocess(
    blankAsUndefined,
    z
      .string()
      .regex(new RegExp(`^${sortSegment}(,${sortSegment})*$`))
      .optional(),
  ),
});

export type RuleTemplatesListQuery = z.infer<
  typeof RuleTemplatesListQuerySchema
>;

/**
 * One row of `GET /risk-analysis/rule-templates`: the template row plus the
 * three counts the list tables show. `ruleCount` used to be sent to Retraced
 * staff only; it is now on every row, so every caller can sort by it.
 */
export const RuleTemplateListRowSchema = RuleTemplateRowSchema.extend({
  blueprintCount: z.number(),
  riskCount: z.number(),
  ruleCount: z.number(),
});

export type RuleTemplateListRow = z.infer<typeof RuleTemplateListRowSchema>;

/*
 * `PUT /risk-analysis/rule-templates/:id` body, ported from `update.body` and
 * `rulesSchemaRequest` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/rule-template.js`.
 *
 * Numbers are coerced, as legacy AJV coerced them — the old frontend's form
 * inputs can send `"2"`. The nested rule shapes are UI select options, so they
 * strip unknown keys as AJV did; only the top-level body is strict.
 */

const ruleNumber = z.coerce.number();
const zeroOrOne = z.coerce.number().pipe(z.union([z.literal(0), z.literal(1)]));

/** An optional, nullable text field; AJV coerced a blank one to `null`. */
const nullableText = (max?: number) =>
  (max === undefined ? z.string() : z.string().max(max))
    .nullable()
    .optional()
    .transform(value => (value === '' ? null : value));

const KeyTextInputSchema = z.object({
  key: z.string().min(1),
  text: z.string().min(1),
});

const RuleAssetTypeEnum = z.enum(
  Object.values(RULE_ASSET_TYPES) as [string, ...string[]],
);

export const RuleOffsetInputSchema = z.object({
  id: z.string().min(1).max(36).optional(),
  trueValue: ruleNumber.pipe(z.number().min(-5).max(5)),
  falseValue: ruleNumber.pipe(z.number().min(-5).max(5)),
  offsetType: z.object({
    key: z.enum(['FIXED', 'OFFSET']),
    text: z.string().min(1),
  }),
  metrics: KeyTextInputSchema,
  riskFactors: z
    .array(
      KeyTextInputSchema.extend({ id: z.string().min(1).max(36).optional() }),
    )
    .min(1),
});

export const RuleTriggerInputSchema = z.object({
  id: z.string().min(1).max(36).optional(),
  idx: ruleNumber.pipe(z.number().min(0)),
  assetType: z.union([
    z.object({
      key: z.union([z.string().min(1), z.number().min(0)]),
      text: z.string().min(1),
    }),
    z.array(KeyTextInputSchema).min(1),
  ]),
  connectorType: KeyTextInputSchema,
  dataPoint: z.object({ key: RuleAssetTypeEnum, text: z.string().min(1) }),
  operator: KeyTextInputSchema.optional(),
  asset: KeyTextInputSchema.extend({
    type: RuleAssetTypeEnum.optional(),
  }).optional(),
});

export const RuleInputSchema = z.object({
  id: z.string().min(1).max(36).optional(),
  idx: ruleNumber.pipe(z.number().min(0)),
  offsets: z.array(RuleOffsetInputSchema).min(1),
  triggers: z.array(RuleTriggerInputSchema).min(1),
});

export type RuleInput = z.infer<typeof RuleInputSchema>;

/**
 * Every field but `isActive` is optional, and an omitted one keeps its stored
 * value — legacy wrote `undefined` over it, so saving a published template's
 * visibility wiped its tags. On a published template only `isActive` is ever
 * written (see `RuleTemplateService.update`).
 */
export const UpdateRuleTemplateBodySchema = z.strictObject({
  title: z.string().min(1).max(255).optional(),
  description: nullableText(4000),
  scoreFormula: nullableText(),
  tags: z.array(z.string().min(1)).nullable().optional(),
  rules: z.array(RuleInputSchema).min(1).nullable().optional(),
  isActive: zeroOrOne,
  defaultSeverity: ruleNumber.pipe(z.number().min(1).max(5)).optional(),
  published: zeroOrOne.optional(),
});

export type UpdateRuleTemplateBody = z.infer<
  typeof UpdateRuleTemplateBodySchema
>;

export const UpdateRuleTemplateParamsSchema = GetRuleTemplateParamsSchema;
